import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, Clock, DollarSign, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { TeamProject } from '@/types/TeamProject';
import { useTeamProject } from '@/hooks/useTeamProjects';
import { useTeamTimeEntries } from '@/hooks/useTeamTimeEntries';
import { TeamProjectMembers } from '@/components/team/TeamProjectMembers';
import { TeamProjectTimeEntries } from '@/components/team/TeamProjectTimeEntries';
import { TeamProjectSettings } from '@/components/team/TeamProjectSettings';

export default function TeamProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: project, isLoading } = useTeamProject(id || '');
  const { entries } = useTeamTimeEntries(id || '');

  // Calculate stats from time entries
  const stats = useMemo(() => {
    if (!entries.length) {
      return { totalHours: 0, totalRevenue: 0 };
    }

    let totalMinutes = 0;
    let totalRevenue = 0;

    entries.forEach(entry => {
      totalMinutes += entry.minutes;
      const hours = entry.minutes / 60;
      const rate = entry.hourly_rate || project?.hourly_rate || 0;
      totalRevenue += hours * rate;
    });

    return {
      totalHours: totalMinutes / 60,
      totalRevenue
    };
  }, [entries, project?.hourly_rate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center">Loading team project...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
              <Button onClick={() => navigate('/team-projects')}>
                Back to Team Projects
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/team-projects')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">{project.name}</h1>
              {project.description && (
                <p className="text-muted-foreground mt-1">{project.description}</p>
              )}
            </div>
            <Badge variant="secondary">
              ${project.hourly_rate}/hr {project.currency}
            </Badge>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Team Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{project.member_count || 0}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Hours Logged</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalHours.toFixed(1)}h</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${stats.totalRevenue.toFixed(2)} {project?.currency || 'USD'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="members" className="space-y-6">
            <TabsList>
              <TabsTrigger value="members">Team Members</TabsTrigger>
              <TabsTrigger value="time-entries">Time Entries</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="members">
              <TeamProjectMembers project={project} />
            </TabsContent>

            <TabsContent value="time-entries">
              <TeamProjectTimeEntries project={project} />
            </TabsContent>

            <TabsContent value="settings">
              <TeamProjectSettings project={project} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}